/**
 * B8 — Dynamic Input overlay for the Three.js renderer.
 *
 * Sibling DOM node to `<Canvas>` (R3F crashes on non-THREE children).
 * Reads cursor via `cursorWorldSnapshot` and camera via
 * `cameraSnapshot`, projects world → client each tick, renders the
 * existing `dynamicInput/` subcomponents (PointerInputFields,
 * DimensionInputField, DynamicPromptLabel).
 *
 * DYNMODE bitmask: bit 1 = pointer, bit 2 = dim, bit 4 = prompts.
 * Three modes (idle / first-pick / second-pick) match the SVG
 * stage's `dynamicInput/DynamicInputOverlay`.
 */

import * as React from "react";
import { useEffect, useReducer, useSyncExternalStore } from "react";

import { useT } from "../../i18n";
import type { DraftSession } from "../../scene/draftingHelpers";
import { buildDraftPrompt, draftStepAcceptsPointInput } from "../../scene/draftingHelpers";
import {
  useSysvarStore,
  selectPointerEnabled,
  selectDimEnabled,
  selectCoordsMode,
  selectInputFormat,
} from "../../dynamicInput/sysvarStore";
import { isEditableTarget as isSysvarEditableTarget } from "../../dynamicInput/sysvarHotkeys";
import {
  reduceFieldEvent,
  buildCommitToken,
  buildDimCommitToken,
  INITIAL_FIELD_STATE,
  type FieldEvent,
} from "../../dynamicInput/fieldBehavior";
import { formatLiveValues, type WorldPoint as DiWorldPoint } from "../../dynamicInput/fieldGeometry";
import { DynamicPromptLabel } from "../../dynamicInput/DynamicPromptLabel";
import { PointerInputFields } from "../../dynamicInput/PointerInputFields";
import { DimensionInputField } from "../../dynamicInput/DimensionInputField";
import { getFieldSpecForStep } from "../../dynamicInput/draftStepFieldConfig";
import { applyOffsetToPoint2d } from "../coordinateOrigin";
import { getCameraVersion, projectWorldToClient, subscribeCameraSnapshot } from "../cameraSnapshot";
import { getCursorWorldSnapshot, getCursorWorldVersion, subscribeCursorWorld } from "./cursorWorldSnapshot";

const POINTER_OFFSET = { dx: 20, dy: 12 } as const;
const PROMPT_OFFSET = { dx: 20, dy: 40 } as const;
const DI_ACTIVATOR_RE = /^[0-9.\-@#=]$/;
/** Steps where a leading digit is a keyword (CIRCLE "2P"/"3P") — never steal. */
const STEPS_WITH_DIGIT_KEYWORDS: ReadonlySet<string> = new Set(["circle-center"]);

export type DynInputOverlayProps = {
  draftSession: DraftSession | null;
  draftReferencePoint: { x: number; y: number } | null;
  activeSpaceName: string;
  onCommit: (text: string) => void;
  onCancel: () => void;
  onOpenSettings?: () => void;
};

function useCameraVersion(): number {
  return useSyncExternalStore(subscribeCameraSnapshot, getCameraVersion, () => 0);
}
function useCursorWorldVersion(): number {
  return useSyncExternalStore(subscribeCursorWorld, getCursorWorldVersion, () => 0);
}

export function DynInputOverlay(props: DynInputOverlayProps): React.ReactElement {
  const t = useT();
  const {
    draftSession,
    draftReferencePoint,
    activeSpaceName,
    onCommit,
    onCancel,
    onOpenSettings,
  } = props;

  // Sysvars
  const pointerEnabled = useSysvarStore(selectPointerEnabled);
  const dimEnabled = useSysvarStore(selectDimEnabled);
  const dynprompt = useSysvarStore((s) => s.values.DYNPROMPT);
  const dynpivis = useSysvarStore((s) => s.values.DYNPIVIS);
  const dyndivis = useSysvarStore((s) => s.values.DYNDIVIS);
  const coordsMode = useSysvarStore(selectCoordsMode);
  const inputFormat = useSysvarStore(selectInputFormat);
  const promptEnabled = dynprompt === 1;
  const alwaysShowFields = dynpivis === 2;

  // Dim-field spec derivation
  const dimSpec = getFieldSpecForStep(draftSession?.step);
  const stepAcceptsPoints =
    draftSession !== null ? draftStepAcceptsPointInput(draftSession.step) : false;
  const dimGateOk =
    draftSession !== null &&
    (!stepAcceptsPoints || draftSession.points.length >= 1);
  const dimActive =
    dimEnabled &&
    draftSession !== null &&
    dimGateOk &&
    dimSpec.kind !== "none" &&
    dyndivis >= 1;
  const dimTwoFields =
    dimActive &&
    dyndivis >= 2 &&
    (dimSpec.kind === "width-height" || dimSpec.kind === "distance-angle");

  // Reducer + snapshot subscriptions
  const [fieldState, dispatch] = useReducer(reduceFieldEvent, INITIAL_FIELD_STATE);
  useCameraVersion();
  useCursorWorldVersion();

  // Cursor → live placeholders → screen px.
  //
  // DRAFT-DOCUMENT-FRAME — the snapshot is the `onCanvasHover` emit
  // (`StageInteractionsBinder` mirrors it), which the pick loop has already
  // put in the DOCUMENT frame, co-space with `draftReferencePoint`
  // (`session.points.at(-1)` / the active point-filter anchor). So the
  // readout and the committed token below are both document values — the
  // absolute readout now shows the coordinate AutoCAD shows, and the
  // relative `@dx,dy` form is a difference of two document points. The
  // SCREEN projection is the one place that needs the render frame back,
  // since `projectWorldToClient` projects through the offset-world camera.
  const cursor = getCursorWorldSnapshot();
  const ref: DiWorldPoint | null = draftReferencePoint ?? null;
  const dimKindHint = dimSpec.kind !== "none" ? dimSpec.kind : undefined;
  const liveValues = cursor
    ? formatLiveValues(cursor, ref, coordsMode, inputFormat, dimKindHint)
    : { primary: "", secondary: "" };
  const cursorRender = cursor ? applyOffsetToPoint2d(cursor) : null;
  const projected = cursorRender
    ? projectWorldToClient(cursorRender.x, cursorRender.y)
    : null;
  const offscreen = { leftPx: -9999, topPx: -9999 };
  const pointerScreen = projected
    ? { leftPx: projected.x + POINTER_OFFSET.dx, topPx: projected.y + POINTER_OFFSET.dy }
    : offscreen;
  const promptScreen = projected
    ? { leftPx: projected.x + PROMPT_OFFSET.dx, topPx: projected.y + PROMPT_OFFSET.dy }
    : offscreen;

  // Auto-activate DI typing on the 2nd point (SVG parity)
  useEffect(() => {
    if (dimActive && !fieldState.typing) {
      dispatch({ type: "begin-typing", initial: "" });
    }
    if (!draftSession && fieldState.typing) {
      dispatch({ type: "cancel" });
    }
  }, [dimActive, draftSession, fieldState.typing]);

  // Global keydown listener — activates typing on first numeric key
  useEffect(() => {
    if (dynpivis === 0) return;
    function onKeyDown(e: KeyboardEvent): void {
      if (!draftSession) return;
      if (fieldState.typing) return;
      if (!DI_ACTIVATOR_RE.test(e.key)) return;
      const noFields =
        dimSpec.kind === "none" && !draftStepAcceptsPointInput(draftSession.step);
      if (noFields) return;
      if (STEPS_WITH_DIGIT_KEYWORDS.has(draftSession.step)) return;
      const target = e.target;
      if (isSysvarEditableTarget(target)) {
        const isCommandLine =
          target instanceof HTMLElement &&
          target.getAttribute("data-testid") === "command-input-inline";
        if (!isCommandLine) return;
      }
      e.preventDefault();
      e.stopPropagation();
      dispatch({ type: "begin-typing", initial: e.key });
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dynpivis, draftSession, fieldState.typing, dimSpec.kind]);

  // Handlers
  function handleEvent(e: FieldEvent): void {
    dispatch(e);
  }
  function handleSubmit(): void {
    // DRAFT-DOCUMENT-FRAME — both operands are DOCUMENT points (see the
    // cursor block above), so an absolute token is the coordinate AutoCAD
    // shows and a relative `@dx<a` token is a document-frame delta. The
    // token round-trips through `submitCommandText` →
    // `useDraftSession.handleDraftTextSubmission` → the draft machine, which
    // reads absolute coordinates as document values — the same grammar a
    // user typing on the command line gets.
    const cur = getCursorWorldSnapshot();
    if (!cur) {
      dispatch({ type: "cancel" });
      return;
    }
    const tokenRef: DiWorldPoint | null = draftReferencePoint ?? null;
    const token = dimActive
      ? buildDimCommitToken(fieldState, dimSpec, cur, tokenRef, dimTwoFields)
      : buildCommitToken(fieldState, coordsMode, inputFormat, cur, tokenRef);
    if (token !== null) {
      onCommit(token);
    } else {
      const empty =
        fieldState.buffer.primary.trim() === "" &&
        fieldState.buffer.secondary.trim() === "" &&
        fieldState.locks.primary === null &&
        fieldState.locks.secondary === null;
      if (empty) onCommit("");
    }
    dispatch({ type: "cancel" });
  }
  function handleEscape(): void {
    onCancel();
    dispatch({ type: "cancel" });
  }

  // Visibility gates
  const showPrompt = promptEnabled && draftSession !== null;
  const showDimFields = dimActive;
  const pointerStepEligible =
    draftSession === null || (dimSpec.kind === "none" && stepAcceptsPoints);
  const showFields =
    !dimActive &&
    pointerEnabled &&
    pointerStepEligible &&
    (alwaysShowFields || fieldState.typing);
  const showTooltip =
    !dimActive &&
    pointerEnabled &&
    pointerStepEligible &&
    !fieldState.typing &&
    draftSession !== null &&
    draftReferencePoint !== null;
  const showCog = dimActive || showFields;
  const promptText = buildDraftPrompt(draftSession, activeSpaceName);

  return (
    <div
      className="dynin-overlay"
      data-testid="dynin-overlay"
      aria-label={t("dynin.overlay.aria")}
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
    >
      <DynamicPromptLabel
        visible={showPrompt}
        text={promptText}
        screenPosition={promptScreen}
      />
      <DimensionInputField
        visible={showDimFields}
        screenPosition={pointerScreen}
        spec={dimSpec}
        state={fieldState}
        liveValues={liveValues}
        twoFields={dimTwoFields}
        onEvent={handleEvent}
        onSubmit={handleSubmit}
        onEscape={handleEscape}
      />
      <PointerInputFields
        visible={showFields}
        screenPosition={pointerScreen}
        state={fieldState}
        mode={coordsMode}
        format={inputFormat}
        liveValues={liveValues}
        onEvent={handleEvent}
        onSubmit={handleSubmit}
        onEscape={handleEscape}
      />
      {showTooltip && (
        <div
          className="dynin-tooltip"
          data-testid="dynin-tooltip"
          aria-hidden="true"
          style={{
            position: "absolute",
            left: pointerScreen.leftPx,
            top: pointerScreen.topPx,
            pointerEvents: "none",
          }}
        >
          {liveValues.primary}
          {coordsMode === "polar" ? " < " : ", "}
          {liveValues.secondary}
        </div>
      )}
      {showCog && onOpenSettings && (
        <button
          type="button"
          className="dynin-cog"
          data-testid="dynin-cog"
          aria-label={t("dynin.overlay.settings")}
          title={t("dynin.overlay.settings")}
          style={{
            position: "absolute",
            left: pointerScreen.leftPx + 96,
            top: pointerScreen.topPx,
            pointerEvents: "auto",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onOpenSettings();
          }}
        >
          {"⚙"}
        </button>
      )}
    </div>
  );
}
